import {
    EApprovalStatus,
    EClientStatus,
    EClientType,
    EDuplicateReasonType,
    EDuplicateResolutionDialogAction,
    EAddressType,
} from '@app/core/enum/client.enum'
import { EApprovalState } from '@app/core/enum/proposal.enum'
import { ITableRow } from '@app/core/models/shared-table.model'

// Pagination Interface
export interface IAPIPagination {
    page: number
    limit: number
    total: number
    total_pages?: number
}

// New API Response Interfaces
export interface IOrganisationResponse {
    success: boolean
    message: string
    data: IOrganisation[]
    pagination: IAPIPagination
}

export interface IAggregatedClientResponse {
    success: boolean
    message: string
    data: IAggregatedClient[]
    pagination: IAPIPagination & { total_pages?: number }
}

export interface IAggregatedClient {
    customer_id: number | string | null
    lead_ids?: number[]
    territory_id?: number
    territory_name?: string
    lead_count?: number
    engagement_type?: string
    organisation_details?: IAggregatedOrganisationDetails | null
    client_data?: IClientDataStructure
    client_request_id?: number
    request_type?: string
    client_type?: string
    request_status?: string
    requested_by?: string
    requested_by_username?: string
    reviewed_by?: string | null
    reviewed_at?: string
    requested_at?: string
    remark?: string
    addresses?: IAggregatedAddress[]
    has_duplicates?: boolean
    duplicates?: IDuplicate[]
}

export interface IOrganisationRequestMetadata {
    engagementTypeName?: string
    customerId?: number | string | null
    leadIds?: number[]
    territoryId?: number
    territoryName?: string

    // Request/approval metadata (for client_data responses)
    clientRequestId?: number
    requestType?: string
    requestStatus?: string
    requestedBy?: string
    requestedByUsername?: string
    reviewedBy?: string | null
    reviewedAt?: string
    requestedAt?: string
    remark?: string
    hasDuplicates?: boolean
    duplicates?: IDuplicate[]
}

export interface IClientDataOrganisation {
    organisation_code?: number
    organisation_name: string
    organisation_type: string
    vertical_type: string
    website?: string | null
    address_line?: string | null
    address_line2?: string | null
    landmark?: string | null
    location?: string | null
    longitude?: number | null
    latitude?: number | null
    ward_id?: number | null
    postal_code?: string | null
    city?: string | null
    district?: string | null
    state?: string | null
    country?: string | null
    area_name?: string | null
    addresses?: IAggregatedAddress[]
    created_by?: string | null
}

export interface IClientDataStructure {
    organisation?: IClientDataOrganisation
    academic_details?: IAggregatedAdditionalDetail
    contacts?: unknown[]
}

export interface IAggregatedOrganisationDetails
    extends Omit<
        IOrganisation,
        'ward' | 'curriculum_details' | 'contact_details' | 'fee_details' | 'additional_details'
    > {
    ward_id?: number | null
    billing_postal_code?: string | null
    status?: boolean
    addresses?: IAggregatedAddress[]
    contact_details?: IAggregatedContactDetail[]
    curriculum_details?: IAggregatedCurriculumDetail[]
    fee_details?: IAggregatedFeeDetail[]
    additional_details?: IAggregatedAdditionalDetail[]
}

export interface IAggregatedAddress {
    address_id: number
    client_reference_id: string
    client_type: string
    address_type: TAddressType
    address_line1: string
    address_line2?: string | null
    landmark?: string | null
    ward?: string | null
    area?: string | null
    city?: string | null
    district?: string | null
    state?: string | null
    postal_code?: string | null
    country?: string | null
    latitude?: number | null
    longitude?: number | null
    location_url?: string | null
    is_active: boolean
    created_by?: string | null
    updated_by?: string | null
    created_at: string
    updated_at: string
}

export interface IAggregatedContactDetail extends IContactDetail {
    contact_is_poc: boolean
}

export interface IAggregatedCurriculumDetail extends ICurriculumDetail {
    organisation_curriculum_id: number
    org_curriculum_id: number
    curriculum_status?: boolean | null
    curriculum_created_by?: string | null
    curriculum_updated_by?: string | null
    curriculum_created_at?: string
    curriculum_updated_at?: string
    status?: boolean | null
}

export function isCurriculumStatusActiveForUi(
    row: { curriculum_status?: boolean | null } | null | undefined,
): boolean {
    return row?.curriculum_status === true
}

export interface IAggregatedFeeDetail extends IFeeDetail {}

export interface IAggregatedAdditionalDetail
    extends Omit<
        IAdditionalDetail,
        | 'additional_details_created_at'
        | 'additional_details_updated_at'
        | 'additional_details_created_by'
        | 'additional_details_updated_by'
        | 'interested_products'
    > {
    interested_products?: string[] | Record<string, boolean> | null
    created_at?: string
    updated_at?: string
    created_by?: string
    updated_by?: string
}

export interface IFeeDetailsItem {
    fees_details_id: number
    organisation_code: number
    monthly_fees: string | number
    annual_fee: string | number
    development_fee: string | number
    smart_class_fee: string | number
    tuition_fee: string | number
    edtech_fee: string | number
    technical_fee: string | number
    created_by: string | null
    updated_by: string | null
    created_at: string
    updated_at: string
    status: boolean
}

export interface IFeeDetailsResponse {
    success: boolean
    message: string
    data: {
        fees: IFeeDetailsItem[]
        pagination: IAPIPagination
    }
}

export interface IOrganisation {
    organisation_code: number
    organisation_name: string
    organisation_type: string
    vertical_type: string
    website?: string
    address_line?: string
    address_line2?: string | null
    landmark?: string
    location?: string
    longitude?: number
    latitude?: number
    ward?: string
    postal_code?: string
    organisation_status?: boolean
    organisation_deleted_at?: string | null
    organisation_created_by?: string | null
    organisation_updated_by?: string
    organisation_created_at?: string
    organisation_updated_at?: string
    contact_details?: IContactDetail[]
    curriculum_details?: ICurriculumDetail[]
    fee_details?: IFeeDetail[]
    academic_fee_details?: IAcademicFeeDetail[]
    group_details?: IGroupDetail[]
    tax_details?: ITaxDetail[]
    additional_details?: IAdditionalDetail[]
    // For aggregated/org endpoints that include addresses array
    addresses?: IAggregatedAddress[]
    area_name?: string | null
    city?: string | null
    district?: string | null
    state?: string | null
    country?: string | null
}

export interface IContactDetail {
    contact_id?: number
    contact_client_type: string | null
    contact_client_ref_id: number
    contact_designation: string
    contact_name: string
    contact_email: string
    contact_email_consent: boolean
    contact_country_code: number
    contact_mobile_number: string
    contact_mobile_consent: boolean
    contact_is_poc?: boolean
    contact_status: boolean
    contact_created_by: string
    contact_updated_by: string
    contact_created_at: string
    contact_updated_at: string
}

export interface ICurriculumDetail {
    curriculum_name: string
    curriculum_detail: ICurriculumDetailInfo
    curriculum_status?: boolean | null
}

export interface IAcademicBoardGradeDisplayRow {
    board: string
    grades: string
}

export interface ISubjectDetail {
    books?: string[]
    topics?: string[]
}

export type ISubjectsMap = Record<string, ISubjectDetail>

export interface ICurriculumDetailInfo {
    grades?: string[]
    subjects?: ISubjectsMap | ISubjectsArray
    academic_year?: string
    special_programs?: string[]
    teaching_methodology?: string
    name?: string
    grade_levels?: string[]
}

export interface ISubjectsArray {
    middle?: ISubject[]
    primary?: ISubject[]
    secondary?: ISubject[]
}

export interface ISubject {
    name: string
    hours_per_week: number
}

export interface IFeeDetail {
    fees_details_id: number
    monthly_fees: number
    annual_fee: number
    development_fee: number
    smart_class_fee: number
    tuition_fee: number
    edtech_fee: number
    technical_fee: number
    fee_created_by: string
    fee_updated_by: string | null
    fee_created_at: string
    fee_updated_at: string
}

export interface IAcademicFeeDetail {
    organisation_academic_fee_id: number
    fee_type: string | null
    grades: string | null
    amount: string | number | null
    curriculum_id: number | null
    fee_curriculum_name: string | null
    fee_status: boolean
    fee_created_by: string | null
    fee_updated_by: string | null
    fee_created_at: string
    fee_updated_at: string
}

export interface IGroupDetail {
    organisation_group_id: number
    organisation_group_name: string
    organisation_group_type: string
    group_status: boolean
    group_created_by: string
    group_updated_by: string
    group_created_at: string
    group_updated_at: string
}

export interface ITaxDetail {
    tax_id?: number
    tax_type?: string
    tax_number?: string
    tax_status?: boolean
    tax_created_by?: string
    tax_updated_by?: string
    tax_created_at?: string
    tax_updated_at?: string
}

export interface IAdditionalDetail {
    additional_id: number
    organisation_code: number
    student_strength: number | null
    teacher_strength: number | null
    lowest_class: string | null
    highest_class: string | null
    board_results: string | null
    year_of_establishment: number | null
    interested_products: string[] | null
    tech_profile: string | null
    description: string | null
    additional_details_created_at: string
    additional_details_updated_at: string
    additional_details_created_by: string
    additional_details_updated_by: string
}

export enum EClientDescriptionHighlightTone {
    Discovery = 'discovery',
    Enrichment = 'enrichment',
}

export interface IClientDescriptionHighlight {
    label: string
    tone: EClientDescriptionHighlightTone
}

export interface IClient extends ITableRow {
    // Basic Information
    schoolName: string
    clientName: string
    studentName?: string
    status: string
    curriculum: string
    class: string
    website: string
    organisationCode?: number
    /** Stable row id for tables (same as organisationCode when mapped from organisation). */
    clientId?: number
    organisationType?: string
    verticalType?: string
    yearOfEstablishment?: number
    description?: string
    descriptionItems?: string[]
    descriptionHighlights?: IClientDescriptionHighlight[]
    sourceLinks?: string[]
    feeRange?: string
    googleRating?: number
    googleUserRatingsTotal?: number
    hideAcademicInfo?: boolean
    hideBillingAddress?: boolean

    // Request/Approval metadata (for client_data responses)
    clientRequestId?: number
    requestType?: string
    requestStatus?: string // PENDING, APPROVE, REJECT
    requestedBy?: string
    requestedByUsername?: string
    reviewedBy?: string | null
    reviewedAt?: string
    requestedAt?: string
    remark?: string
    hasDuplicates?: boolean
    duplicates?: IDuplicate[]

    // Location Information
    addressLine?: string
    landmark?: string
    location?: string
    longitude?: number
    latitude?: number
    ward?: string
    postalCode?: string
    areaName?: string
    city?: string
    district?: string
    state?: string
    country?: string

    // Academic Information
    primaryBoard?: string
    secondaryBoard?: string
    studentStrength?: number
    teacherStrength?: number
    tenthBoardResult?: string
    lowestClass?: string
    highestClass?: string
    curriculumDetails?: ICurriculumDetail[]
    techProfile?: string

    // Contact Information
    contacts: IContact[]
    /** Contact name where contact_is_poc is true (for table display). */
    contactNamePoc?: string
    shippingAddress: IAddress
    billingAddress?: IAddress
    isBillingAddressSameAsShipping: boolean

    // Financial Information
    annualFee?: number
    monthlyFees?: number
    tuitionFee?: number
    techFee?: number
    edtechFee?: number
    smartClassFee?: number
    developmentFee?: number
    otherFee?: number
    budgetEstimate?: number
    source?: string
    feeDetails?: IFeeDetail[]
    academicFeeDetails?: IAcademicFeeDetail[]

    // Tax Information
    tan?: string
    gst?: string
    taxDetails?: ITaxDetail[]

    // Additional Information
    memberSchools?: string[]
    interestedProducts: string[]
    groupDetails?: IGroupDetail[]
    additionalDetails?: IAdditionalDetail[]

    // Card Display Fields
    clientType?: string
    engagement?: string
    date?: string
    deals?: number
    productTags?: string[]

    // Audit Information
    createdAt?: string
    updatedAt?: string
    createdBy?: string
    updatedBy?: string
}

export interface IContact {
    id?: number
    name: string
    role: string
    mobileNumber: string
    landlineNumber?: string
    email: string
    countryCode?: number
    emailConsent?: boolean
    mobileConsent?: boolean
    status?: boolean
}

export interface IContactRoleLike {
    role?: string | null
}

export interface IOrganisationContactApiItem {
    contact_id: number
    client_type?: string
    client_reference_id?: number
    designation?: string
    name?: string
    email?: string
    email_consent?: boolean
    country_code?: number
    mobile_number?: string
    mobile_consent?: boolean
    is_poc?: boolean
    status?: boolean
    user_id?: string
    created_by?: string
    updated_by?: string
    created_at?: string
    updated_at?: string
}

export interface IOrganisationContactsListResponse {
    success: boolean
    message: string
    data: IOrganisationContactApiItem[]
    pagination?: {
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

export interface IAddress {
    line1?: string
    line2?: string
    landmark?: string
    area?: string
    city?: string
    district?: string
    state?: string
    postal_code?: string
    country?: string
    phoneNumber?: string
}

export interface IOrganizationAddressPayload {
    client_reference_id: string
    client_type: string
    address_type: EAddressType
    address_line1: string
    city: string
    state: string
    postal_code: string
    landmark: string
    address_line2?: string
    ward?: string
    area?: string
    district?: string
    country?: string
    latitude?: number | null
    longitude?: number | null
    location_url?: string
}

export interface IClientSearchQueryState {
    apiSearchTerm: string
    meetsMinLength: boolean
    shouldRefetch: boolean
    showMinLengthError: boolean
}

// Search Result Interface
export interface ISearchResult {
    organisation_name: string
    organisation_code: number
    trigram_similarity: number
    phonetic_similarity: number
    combined_score: number
    location?: string
    email?: string
    created_at?: string
    postal_code?: string
    postal_code_city?: string
    address?: string
}

// Funder Search Result Interface (from funder fuzzy search API)
export interface IFunderSearchResult {
    id: number
    name: string
    similarity_score: number
    exact_match: boolean
    matched_acronym: string | null
    address: {
        area: string | null
        ward: string | null
        city: string | null
        district: string | null
        state: string | null
        postal_code: string | null
        country: string | null
    }
}

// Funder Search Response Interface
export interface IFunderSearchResponse {
    success: boolean
    message: string
    data: IFunderSearchResult[]
    pagination: {
        page: number
        limit: number
        total: number
    }
}

// Search Response Interface
export interface ISearchResponse {
    success: boolean
    message: string
    data:
        | ISearchResult[]
        | {
              results: ISearchResult[]
              pagination: {
                  total: number
                  page: number
                  limit: number
              }
          }
    pagination?: {
        total: number
        page: number
        limit: number
        totalPages: number
    }
}

// Payload types for create client request
export interface IClientContactPayload {
    client_type: EClientType.ORGANISATION
    designation: string
    name: string
    email: string
    email_consent: boolean
    country_code: string
    mobile_number: string
    mobile_consent: boolean
    is_poc?: boolean
    created_by: string
}

export interface IClientTaxPayload {
    client_type: EClientType.ORGANISATION
    gst: string
    tan: string
    pan: string
    billing_name: string
    billing_address: string
    created_by: string
}

export interface IClientFeePayload {
    monthly_fees?: number | null
    annual_fee?: number | null
    development_fee?: number | null
    smart_class_fee?: number | null
    tuition_fee?: number | null
    edtech_fee?: number | null
    technical_fee?: number | null
    created_by: string
}

export interface IClientFeeItemPayload {
    fee_type: string
    grades: string
    amount: number
    curriculum_id: number | null
}

export interface IClientCurriculumPayload {
    curriculum_id: number
    curriculum_detail: {
        grades: {
            grade: number
            student_strength: number
        }[]
    }
}

export interface IClientAcademicDetailsPayload {
    student_strength?: number | null
    teacher_strength?: number | null
    lowest_class?: string
    highest_class?: string
    board_results?: string
    year_of_establishment?: number | null
    interested_products?: Record<string, boolean>
    tech_profile?: {
        computers?: number | null
        projectors?: number | null
        internet?: boolean
        [key: string]: boolean | number | null | undefined
    }
    description?: string
    annual_fees?: {
        board: string
        grade: number
        amount?: string
        student_strength?: string
    }[]
    other_fees?: {
        fee_type: string
        grades: number[]
        amount: string
    }[]
    created_by: string
}

export type TAddressType = 'billing' | 'shipping' | 'both'

export interface IClientAddressPayload {
    address_type: TAddressType
    address_line1: string
    postal_code: string
    city: string
    state: string
    country: string
    landmark?: string
    address_line2?: string
    ward?: string
    area?: string
    district?: string
    latitude?: number | null
    longitude?: number | null
    location_url?: string | null
}

export interface IClientCreateRequestPayload {
    request_type: EClientStatus.CREATE
    client_type: EClientType.ORGANISATION
    request_status?: EClientStatus.PENDING
    client_data: {
        organisation: {
            organisation_name?: string
            organisation_type: EClientType.SCHOOL
            vertical_type: string
            address_line?: string
            landmark?: string
            location?: string
            longitude?: number | null
            latitude?: number | null
            ward_id?: number | null
            postal_code: string
            created_by: string
            website?: string
        }
        address?: IClientAddressPayload[]
        contacts?: IClientContactPayload[]
        tax?: IClientTaxPayload[]
        fees?: IClientFeePayload[]
        fee_items?: IClientFeeItemPayload[]
        curriculum?: IClientCurriculumPayload[]
        academic_details?: IClientAcademicDetailsPayload
    }
    requested_by?: string
    remark?: string
}

export interface IClientRequestResponse<T = unknown> {
    success: boolean
    message: string
    data: T
}

export interface IPotentialDuplicatesResponse {
    success: boolean
    message: string
    data: {
        has_duplicates: boolean
        duplicates: IDuplicate[]
    }
}

export interface IClientCreationResponseData {
    funder_id?: number
    organisation_code?: number
    organization_code?: number
    organisationCode?: number
}

export interface IFunderCreationResponseData {
    funder_id: number
}

export interface IFilterDropdownState {
    isStatusDropdownOpen: boolean
    isCurriculumDropdownOpen: boolean
    isSortDropdownOpen: boolean
}

export interface IFilterChangeEvent {
    type: 'status' | 'curriculum' | 'sort'
    value: string
}

export interface IClickOutsideEvent {
    target: Node
    type: 'click' | 'touchend'
}

interface IClientTaxInfo {
    gst: string
    pan: string
    tan: string
    created_by: string
    client_type: string
    billing_name: string
    billing_address: string
}

interface IClientFees {
    annual_fee: number
    created_by: string
    edtech_fee: number
    tuition_fee: number
    monthly_fees: number
    technical_fee: number
    development_fee: number
    smart_class_fee: number
}

interface IClientContact {
    name: string
    email: string
    created_by: string
    client_type: string
    designation: string
    country_code: number
    email_consent: boolean
    mobile_number: string
    mobile_consent: boolean
}

export interface ICurriculumDetail {
    detail?: string
}

interface IClientCurriculum {
    created_by: string
    curriculum_id: number
    curriculum_detail: ICurriculumDetail
}

interface IClientOrganisation {
    ward_id: number
    landmark: string
    latitude: number
    location: string
    longitude: number
    created_by: string
    postal_code: string
    address_line: string
    vertical_type: string
    organisation_name: string
    organisation_type: string
    country: string
    request_status: string
}

export interface IClientDataBillingAddress {
    area?: string
    city?: string
    state?: string
    country?: string
    district?: string
    landmark?: string
    latitude?: number
    longitude?: number
    postal_code?: string
    location_url?: string
    address_line1?: string
    address_line2?: string
    ward?: string
    location?: string
}

export interface IClientDataAddress {
    area?: string
    city?: string
    state?: string
    country?: string
    district?: string
    landmark?: string
    postal_code?: string
    address_type?: string
    address_line1?: string
    address_line2?: string
}

interface IClientAcademicDetails {
    created_by: string
    description: string
    lowest_class: string
    tech_profile: {
        internet: boolean | string
        computers: number
        projectors: number
        [key: string]: boolean | string | number | undefined
    }
    board_results: string
    highest_class: string
    student_strength: number
    teacher_strength: number
    interested_products: {
        edtech: boolean
        library: boolean
        smart_class: boolean
        [key: string]: boolean
    }
    year_of_establishment: number
}

export interface IClientData {
    tax: IClientTaxInfo[] | undefined
    fees: IClientFees[] | undefined
    contacts: IClientContact[] | undefined
    curriculum: IClientCurriculum[] | undefined
    organisation: IClientOrganisation | undefined
    academic_details: IClientAcademicDetails | undefined
    address?: IClientDataAddress[]
    billing_address?: IClientDataBillingAddress
}
// Duplicate reason types with type-safe data structures
export type DuplicateReasonType =
    | 'Email'
    | 'Pincode'
    | 'OrganisationName'
    | 'Phone'
    | 'POCName'
    | 'Domain'
    | 'Address'

export interface IEmailDuplicateReason {
    reason: 'Email'
    data: {
        email: string
    }
}

export interface IPincodeDuplicateReason {
    reason: 'Pincode'
    data: {
        postal_code: string
    }
}

export interface IOrganisationNameDuplicateReason {
    reason: 'OrganisationName'
    data: {
        organisation_name: string
    }
}

export interface IPhoneDuplicateReason {
    reason: 'Phone'
    data: {
        phone_number: string
        country_code: string
    }
}

export interface IPOCNameDuplicateReason {
    reason: 'POCName'
    data: {
        name: string
    }
}

export interface IDomainDuplicateReason {
    reason: 'Domain'
    data: {
        website: string | null
        email: string | null
    }
}

export interface IAddressDuplicateReason {
    reason: 'Address'
    data: {
        address_line: string
        landmark: string
        location?: string
        postal_code: string
    }
}

// Discriminated union for type-safe duplicate reasons
export type IDuplicateReason =
    | IEmailDuplicateReason
    | IPincodeDuplicateReason
    | IOrganisationNameDuplicateReason
    | IPhoneDuplicateReason
    | IPOCNameDuplicateReason
    | IDomainDuplicateReason
    | IAddressDuplicateReason

export interface IDuplicate {
    organisation_code: number
    organisation_name: string
    score: number
    scorePercentage: number
    reasons: IDuplicateReason[]
}

export interface IUnapprovedClientData {
    client_request_id: number
    request_type: string
    client_id: string | null
    client_type: string
    client_data: IClientData
    request_status: string
    remark: string | null
    requested_by: string
    requested_by_username: string
    reviewed_by: string | null
    requested_at: string
    reviewed_at: string
    addresses?: IAddress[]
    has_duplicates: boolean
    duplicates: IDuplicate[]
}

export interface IDuplicateResolutionDialogData {
    duplicates: IDuplicate[]
}

export interface IDuplicateResolutionDialogResult {
    action: EDuplicateResolutionDialogAction
}

export interface IDuplicateTableRow extends ITableRow {
    organisation_code: number
    organisation_name: string
    reasons: IDuplicateReason[]
    scorePercentage: number
}

export interface IDuplicateTableConfigOptions {
    linkCssClass: string
    reasonsListCssClass: string
    reasonItemCssClass: string
    useConstants?: boolean
}

export interface IUnapprovedClientResponse {
    success: boolean
    message: string
    data: IUnapprovedClientData[]
    pagination: IAPIPagination
}

export interface ICSLPaginationState {
    currentPage: number
    pageSize: number
    totalItems: number
    totalPages: number
}

export interface ICSLPaginationParams {
    page: number
    limit: number
    request_status?: string
    search?: string
    filter?: string
}

export interface IClientRequestAction {
    action: EApprovalStatus
    reviewed_by: string
    remark: string
}

export interface IClientRequestActionResponse {
    success: boolean
    message: string
    data?: unknown
}

export interface IAdditionalDetailsPayload {
    student_strength?: number
    teacher_strength?: number
    year_of_establishment?: number
    lowest_class?: string
    highest_class?: string
}

export interface IAdditionalDetailsResponse {
    success: boolean
    message: string
}

// Academic details list (new GET API)
export interface IAcademicDetailsItem {
    additional_id: number
    organisation_code: number
    student_strength: number | null
    teacher_strength: number | null
    lowest_class: string | null
    highest_class: string | null
    year_of_establishment: number | null
    interested_products: Record<string, boolean> | null
    tech_profile: Record<string, unknown> | null
    description: string | null
    created_at: string
    updated_at: string
    created_by: string
    updated_by: string
    edtech_vendor: string | null
    board_results: string | null
}

export interface IAcademicDetailsListResponse {
    success: boolean
    message: string
    data: {
        academic_details: IAcademicDetailsItem[]
        page: number
        limit: number
        total: number
    }
}

export interface ICurriculumDetailPayload {
    primary_board: string
    secondary_board?: string
    medium?: string
    class_10th_result?: number
    class_12th_result?: number
}

export interface IOrganisationCurriculumUpdatePayload {
    curriculum_detail: ICurriculumDetailPayload
}

export interface IOrganisationCurriculumUpdateResponse {
    success: boolean
    message: string
    data?: {
        organisation_curriculum_id: number
        organisation_code: number
        curriculum_id: number
        curriculum_detail: ICurriculumDetailPayload
        status: boolean
        created_by: string
        updated_by: string
        created_at: string
        updated_at: string
    }
}

export interface ICurriculumConfig {
    id: number
    name: string
}
export interface IOrganisationUpdatePayload {
    name?: string
    type?: string
    address_line?: string
    website?: string
}

export interface IOrganisationUpdateResponse {
    success: boolean
    message: string
}

export type TPercentageKey = 'class_10th_result' | 'class_12th_result'

export type TContactSearchField = 'name' | 'email' | 'mobileNumber' | 'role'

export interface IClientContactBookModalData {
    contacts?: IContact[]
    organisationCode: string
}
